import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { keyBy } from 'lodash-es';
import { API_BASE_PLATFORM_URL } from '@core/index';
import { LineOfBusiness, ProductKey } from '@core/enumeration';
import { ProductConfiguration, ProductWithLoBAndTenantDTO } from '@shared/model';
import { dailyDashboardProductConfig } from '@shared/constants';
import { SecurityService, SessionStorageService } from '@mynexus/mynexus-ui-lib';

export interface LineOfBusinessReference {
  lineOfBusinessId: string;
  description: string;
  applicationKey: LineOfBusiness;
}

type LineOfBusinessMap = Record<LineOfBusiness, LineOfBusinessReference>;

@Injectable({
  providedIn: 'root'
})
export class PlatformService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(API_BASE_PLATFORM_URL);
  private readonly securityService = inject(SecurityService);
  private readonly sessionStorage = inject(SessionStorageService);
  private readonly lineOfBusinessMap$ = new BehaviorSubject<LineOfBusinessMap>(null);

  readonly currentProduct = new BehaviorSubject<ProductKey | null>(null);
  readonly currentLineOfBusiness$ = new BehaviorSubject<LineOfBusinessReference | null>(null);

  getLineOfBusinesses(): Observable<LineOfBusinessMap> {
    if (this.lineOfBusinessMap$.value) {
      return this.lineOfBusinessMap$;
    }

    return this.http.get<LineOfBusinessReference[]>(`${this.apiUrl}/LineOfBusiness`)
      .pipe(
        tap((res) => this.lineOfBusinessMap$.next(keyBy(res, 'applicationKey') as LineOfBusinessMap)),
        map(() => this.lineOfBusinessMap$.value),
      );
  }

  getProductsWithLinesOfBusiness(): Observable<ProductWithLoBAndTenantDTO[]> {
    return this.http.get<ProductWithLoBAndTenantDTO[]>(`${this.apiUrl}/Product/GetProductsWithLinesOfBusiness`);
  }

  setCurrentProduct(product: ProductKey) {
    this.currentProduct.next(product);
    this.sessionStorage.setItem('product', product);

    const productsConfig = keyBy(dailyDashboardProductConfig, 'option.value') as Record<ProductKey, ProductConfiguration>;
    const selectedLOB = productsConfig[product].option.lineOfBusiness as LineOfBusiness;

    if (this.lineOfBusinessMap$.value) {
      this.currentLineOfBusiness$.next(this.lineOfBusinessMap$.value[selectedLOB]);
    }
  }

  getDefaultProduct(): void {
    const savedProduct = this.sessionStorage.getItem('product');

    const defaultProduct = dailyDashboardProductConfig.find((product) =>
      (!savedProduct || product?.option.value === savedProduct) && this.securityService.hasAccess(product.access)
    );

    if (!defaultProduct && savedProduct) {
      this.sessionStorage.removeItem('product');
    }

    const defaultProductName = defaultProduct?.option.value || null;

    if (defaultProductName) {
      this.setCurrentProduct(defaultProductName);
    }
  }
}
